import React, { useState, useEffect } from 'react';
import { useOrderModal } from '../contexts/OrderModalContext';

const OrderModal = () => {
  const { isOpen, preselectedLocation, orderType, closeModal } = useOrderModal();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch locations when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchLocations();
    }
  }, [isOpen]);

  // Set preselected location when available
  useEffect(() => {
    
    
    if (preselectedLocation && locations.length > 0) {
      
      
      const location = locations.find(loc => {
        const nameSlug = loc.name.toLowerCase().replace(/\s+/g, '-');
        
        return nameSlug === preselectedLocation || loc._id === preselectedLocation;
      });
      
      
      
      if (location) {
        setSelectedLocation(location);
      }
    }
  }, [preselectedLocation, locations]);

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/locations');
      const data = await response.json();
      
      if (data.success) {
        
        setLocations(data.data);
      } else {
        setError('Failed to load locations');
      }
    } catch (err) {
      setError('Failed to load locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handlePickupClick = () => {
    
    
    if (selectedLocation?.pickupUrl) {
      
      window.open(selectedLocation.pickupUrl, '_blank');
      closeModal();
    } else {
      
    }
  };

  const handleDeliveryClick = () => {

    if (selectedLocation?.deliveryUrl) {
      window.open(selectedLocation.deliveryUrl, '_blank');
      closeModal();
    } else {
      
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Get content based on order type
  const getModalContent = () => {
    if (orderType === 'pickup') {
      return {
        title: 'Order for Pickup',
        description: 'Choose your location and we\'ll have your order ready for pickup.',
        buttonText: 'Order for Pickup',
        buttonAction: handlePickupClick,
        buttonClass: ''
      };
    } else if (orderType === 'delivery') {
      return {
        title: 'Order for Delivery',
        description: 'Choose your location and we\'ll deliver fresh to your door.',
        buttonText: 'Order for Delivery',
        buttonAction: handleDeliveryClick,
        buttonClass: ''
      };
    } else {
      // Default: show both options
      return {
        title: 'Order For Pickup or Delivery',
        description: 'Choose your location and how you\'d like to receive your order.',
        showBothButtons: true
      };
    }
  };

  const modalContent = getModalContent();

  if (!isOpen) return null;

  return (
    <div 
      className="modal is-active"
      onClick={handleOverlayClick}
    >
      <div className="modal-background" />
      <div className="modal-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <header className="mb-0 pb-0 modal-card-head backgroundColorWhite">
          <h2 className="my-0 py-0 modal-card-title orderNowModal colorBlack">{modalContent.title}</h2>
          <button 
            className="delete" 
            aria-label="close"
            onClick={closeModal}
          />
        </header>
        
        <section className="modal-card-body backgroundColorWhite">
          {/* Description based on order type */}
          <p className="mb-4 colorBlack">{modalContent.description}</p>
          
          {loading && (
            <div className="has-text-centered">
              <div className="loader"></div>
            </div>
          )}
          
          {error && (
            <div className="notification is-danger">
              <p>{error}</p>
              <button className="button is-small" onClick={fetchLocations}>
                Retry
              </button>
            </div>
          )}
          
          {!loading && !error && (
            <>
              <div className="field">
                {/* <label className="label">Choose your location:</label> */}
                <div className="control">
                  <div className="location-selector-container is-fullwidth">
                    <select 
                      className='location-selector'
                      value={selectedLocation?._id || ''} 
                      onChange={(e) => {
                        const location = locations.find(loc => loc._id === e.target.value);
                        handleLocationSelect(location);
                      }}
                    >
                      <option value="">Select a location...</option>
                      {locations.map((location) => (
                        <option key={location._id} value={location._id}>
                          {location.name} - {location.address?.address1}, {location.address?.city}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              {selectedLocation && (
                <div className="content">
                  <p className='py-0 my-0 is-bold colorGreen'>Selected Location:</p>
                  <p className='py-0 colorBlack'><span className='is-bold'>{selectedLocation.name}</span><br />
                    {selectedLocation.address?.address1}<br />
                    {selectedLocation.address?.city}, {selectedLocation.address?.state} {selectedLocation.address?.zip}
                  </p>
                </div>
              )}
            </>
          )}
        </section>
        
        <footer className="modal-card-foot backgroundColorWhite">
          <div className="buttons fullWidth is-flex is-justify-content-space-between">
            {modalContent.showBothButtons ? (
              // Show both buttons (default behavior)
              <>
                <button 
                  className={`button fullWidth ${!selectedLocation ? 'is-disabled' : ''}`}
                  onClick={handlePickupClick}
                  disabled={!selectedLocation}
                >
                  Order for Pickup
                </button>
                <button 
                  className={`button fullWidth ${!selectedLocation ? 'is-disabled' : ''}`}
                  onClick={handleDeliveryClick}
                  disabled={!selectedLocation}
                >
                  Order for Delivery
                </button>
              </>
            ) : (
              // Show single button based on order type
              <button 
                className={`button ${modalContent.buttonClass} ${!selectedLocation ? 'is-disabled' : ''}`}
                onClick={modalContent.buttonAction}
                disabled={!selectedLocation}
              >
                {modalContent.buttonText}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default OrderModal;